import { bufferTime, EMPTY, filter, map } from 'rxjs';

import type {
  DestinationAvailabilityCriteria,
  SearchService,
  DestinationAvailabilityResponse,
  ProductCheapestAvailabilityResponse,
  GraphQL,
} from '@libs/trip-planner-interfaces';
import { isDefined, logged, performanceMark } from '@libs/utils';

import { getApolloClient } from './infrastructure';
import { SEARCH_DESTINATION_AVAILABILITY_SUBSCRIPTION } from './queries';
import { getCheapestStayByHotelRequestQL } from './utils/requestMappers/cheapestStayByHotelRequestQL';
import { toDestinationAvailabilityResponseQL } from './utils/responseMappers/destinationAvailabilityResponseQL';

export class DestinationAvailabilitySearchService
  implements
    SearchService<
      DestinationAvailabilityCriteria,
      ProductCheapestAvailabilityResponse[]
    >
{
  private readonly bufferTimeMilliseconds = 100;

  public search(
    criteria: DestinationAvailabilityCriteria
  ): DestinationAvailabilityResponse {
    if (!criteria.userCriteria.recipe.ingredients) {
      return EMPTY;
    }

    const request = getCheapestStayByHotelRequestQL(criteria);

    performanceMark('DestinationAvailabilityRequested');

    const availabilityResponse = getApolloClient().subscribe<
      GraphQL.searchDestinationAvailabilitySubscription,
      GraphQL.searchDestinationAvailabilitySubscriptionVariables
    >({
      variables: {
        request,
      },
      query: SEARCH_DESTINATION_AVAILABILITY_SUBSCRIPTION,
    });

    return availabilityResponse.pipe(
      logged('Destination Availability Response'),
      map((fetchResult) => fetchResult.data),
      filter(isDefined),
      map(toDestinationAvailabilityResponseQL),
      bufferTime(this.bufferTimeMilliseconds),
      filter(
        (cheapestAvailability) => Object.keys(cheapestAvailability).length > 0
      )
    );
  }
}
